import React from 'react';
import { cn } from '@/lib/core/utils';

export type MinimizedShiftStatus = 'pending' | 'in_progress' | 'completed' | 'planning' | 'unassigned';

interface MinimizedShiftProps {
  title: string;
  startHour: number;
  endHour: number;
  employee: string;
  status: MinimizedShiftStatus;
  className?: string;
}

const statusColorClasses: Record<MinimizedShiftStatus, string> = {
  pending: 'group-hover:bg-blue-500/70 group-hover:border-blue-400/50',
  in_progress: 'group-hover:bg-emerald-500/70 group-hover:border-emerald-400/50',
  completed: 'group-hover:bg-green-500/70 group-hover:border-green-400/50',
  planning: 'group-hover:bg-indigo-500/70 group-hover:border-indigo-400/50',
  unassigned: 'group-hover:bg-primary/70 group-hover:border-primary/50'
};


/**
 * MinimizedShift - A compact, reusable shift display component
 *
 * Mirrors the layout structure of DraggableShift but simplified for
 * landing pages, previews, and static displays.
 *
 * Layout:
 * ┌─────────────────────────────────────┐
 * │ Title          │ Status │ Employee │
 * │ Time · Duration│  Dot   │  Badge   │
 * └─────────────────────────────────────┘
 *
 * Supports group-hover color reveal when used inside a container with `group` class.
 */
export const MinimizedShift: React.FC<MinimizedShiftProps> = ({
  title,
  startHour,
  endHour,
  employee,
  status,
  className
}) => {
  const duration = endHour - startHour;

  return (
    <div
      className={cn(
        "h-full rounded flex items-center gap-1.5 px-1.5",
        "border transition-all duration-300",
        "bg-muted-foreground/30 border-muted-foreground/20",
        "text-foreground/70 group-hover:text-white",
        statusColorClasses[status],
        className
      )}
    >
      {/* Left: Title + Time (stacked) */}
      <div className="flex flex-col justify-center min-w-0 flex-1 py-0.5">
        <span className="text-[9px] font-medium truncate leading-tight">
          {title}
        </span>
        <span className="text-[7px] opacity-80 leading-tight">
          {startHour}:00-{endHour}:00 · {duration}h
        </span>
      </div>

      {/* Right: Employee badge */}
      <span className={cn(
        "w-4 h-4 rounded-full text-[7px] font-bold",
        "flex items-center justify-center shrink-0",
        "transition-colors duration-300",
        "bg-background/80 text-foreground/60",
        "group-hover:bg-white/90 group-hover:text-gray-700"
      )}>
        {employee}
      </span>
    </div>
  );
};

export default MinimizedShift;
